import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { PdfRouter } from '@/pdf/PdfRouter';
import { SessionProvider } from '@/lib/session';
import { ScrollManager, SiteLayout } from '@/components/layout/SiteLayout';
import { HomePage } from '@/pages/HomePage';
import { ModulePage, PlatformOverviewPage } from '@/pages/PlatformPages';
import { HardwarePage } from '@/pages/HardwarePage';
import { CloudPage } from '@/pages/CloudPage';
import { PricingPage } from '@/pages/PricingPage';
import { LicenseBuilderPage } from '@/pages/LicenseBuilderPage';
import { CertificationPage, TrainingPage } from '@/pages/TrainingPages';
import { PartnersPage } from '@/pages/PartnersPage';
import { OrderPage } from '@/pages/OrderPage';
import { CompanyPage, ContactPage, IndustriesPage, NotFoundPage, ResourcesPage } from '@/pages/InfoPages';
import { ForgotPasswordPage, LoginPage, RegisterPage, ResetPasswordPage } from '@/pages/AuthPages';
import { PortalLayout } from '@/portal/PortalLayout';
import { OverviewPage } from '@/portal/OverviewPage';
import { ApplianceDetailPage, AppliancesPage } from '@/portal/AppliancesPages';
import { BillingPage } from '@/portal/BillingPage';
import { NotificationsPage } from '@/portal/NotificationsPage';
import { AccountPage, AiUsagePage } from '@/portal/ServicesPages';
import { ArchivePage } from '@/portal/ArchivePages';
import { UpdatesPage } from '@/portal/UpdatesPage';
import { RemoteInvitePage, RemoteViewingPage } from '@/portal/RemotePages';
import { AppUsersPage } from '@/portal/AppUsersPage';
import { LicensesPage, NewLicensePage } from '@/portal/LicensesPages';
import { Loading } from '@/portal/components';

// The staff console is its own chunk: public visitors never download it.
const AdminRoot = lazy(() => import('@/admin/AdminLayout').then((m) => ({ default: m.AdminRoot })));
const AdminLayout = lazy(() => import('@/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const AdminLoginPage = lazy(() => import('@/admin/AdminLayout').then((m) => ({ default: m.AdminLoginPage })));
const AdminOverviewPage = lazy(() => import('@/admin/AccountsPages').then((m) => ({ default: m.AdminOverviewPage })));
const AdminAccountsPage = lazy(() => import('@/admin/AccountsPages').then((m) => ({ default: m.AdminAccountsPage })));
const AdminAccountPage = lazy(() => import('@/admin/AccountsPages').then((m) => ({ default: m.AdminAccountPage })));
const AdminLicensesPage = lazy(() => import('@/admin/OperationsPages').then((m) => ({ default: m.AdminLicensesPage })));
const AdminApplicationsPage = lazy(() => import('@/admin/OperationsPages').then((m) => ({ default: m.AdminApplicationsPage })));
const AdminOrdersPage = lazy(() => import('@/admin/OperationsPages').then((m) => ({ default: m.AdminOrdersPage })));
const AdminReleasesPage = lazy(() => import('@/admin/ReleasesPage').then((m) => ({ default: m.AdminReleasesPage })));
const AdminStaffPage = lazy(() => import('@/admin/OperationsPages').then((m) => ({ default: m.AdminStaffPage })));
const AdminHelpPage = lazy(() => import('@/admin/HelpPage').then((m) => ({ default: m.AdminHelpPage })));
const AdminTrainingPage = lazy(() => import('@/admin/TrainingPages').then((m) => ({ default: m.AdminTrainingPage })));
const AdminTrainingStudentPage = lazy(() => import('@/admin/TrainingPages').then((m) => ({ default: m.AdminTrainingStudentPage })));

/** The printable datasheets live at /#pdf/<slug>, outside the site chrome. */
function getPdfSlug(): string | null {
  const match = window.location.hash.match(/^#pdf\/(.+)$/);
  return match ? match[1] : null;
}

export function App() {
  const [pdfSlug, setPdfSlug] = useState<string | null>(getPdfSlug());

  useEffect(() => {
    const onHashChange = () => setPdfSlug(getPdfSlug());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (pdfSlug) {
    return <PdfRouter slug={pdfSlug} />;
  }

  return (
    <BrowserRouter>
      <SessionProvider>
        <ScrollManager />
        <Routes>
          <Route element={<SiteLayout />}>
            <Route index element={<HomePage />} />
            <Route path="platform" element={<PlatformOverviewPage />} />
            <Route path="platform/:slug" element={<ModulePage />} />
            <Route path="hardware" element={<HardwarePage />} />
            <Route path="cloud" element={<CloudPage />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="license-builder" element={<LicenseBuilderPage />} />
            <Route path="industries" element={<IndustriesPage />} />
            <Route path="training" element={<TrainingPage />} />
            <Route path="training/certification" element={<CertificationPage />} />
            <Route path="partners" element={<PartnersPage />} />
            <Route path="order" element={<OrderPage />} />
            <Route path="resources" element={<ResourcesPage />} />
            <Route path="company" element={<CompanyPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />
            <Route path="remote/invite" element={<RemoteInvitePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="portal" element={<PortalLayout />}>
            <Route index element={<OverviewPage />} />
            <Route path="appliances" element={<AppliancesPage />} />
            <Route path="appliances/:id" element={<ApplianceDetailPage />} />
            <Route path="licenses" element={<LicensesPage />} />
            <Route path="licenses/new" element={<NewLicensePage />} />
            <Route path="updates" element={<UpdatesPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="ai" element={<AiUsagePage />} />
            <Route path="archive" element={<ArchivePage />} />
            <Route path="remote" element={<RemoteViewingPage />} />
            <Route path="app-users" element={<AppUsersPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route
            path="admin"
            element={
              <Suspense fallback={<Loading />}>
                <AdminRoot />
              </Suspense>
            }
          >
            <Route path="login" element={<AdminLoginPage />} />
            <Route element={<AdminLayout />}>
              <Route index element={<AdminOverviewPage />} />
              <Route path="accounts" element={<AdminAccountsPage />} />
              <Route path="accounts/:id" element={<AdminAccountPage />} />
              <Route path="licenses" element={<AdminLicensesPage />} />
              <Route path="applications" element={<AdminApplicationsPage />} />
              <Route path="orders" element={<AdminOrdersPage />} />
              <Route path="releases" element={<AdminReleasesPage />} />
              <Route path="training" element={<AdminTrainingPage />} />
              <Route path="training/:id" element={<AdminTrainingStudentPage />} />
              <Route path="staff" element={<AdminStaffPage />} />
              <Route path="help" element={<AdminHelpPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}
